// src/context/PermissionsContext.tsx
// Fetches Django admin permissions (scopes/capabilities) and exposes
// convenient guards for gating UI modules.

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { getPermissions, type PermissionsResponse, type Capability } from '@/lib/api_travel';

interface PermissionsCtx {
  /** Raw scopes from the API key, e.g. ["meta:cto","meta:create","travel:admin"] */
  scopes: string[];
  /** Full capabilities list */
  capabilities: Capability[];
  /** Quick check: does the token have a specific scope? */
  hasScope: (scope: string) => boolean;
  /** Quick check: does ANY scope start with prefix? e.g. hasPrefix("meta") */
  hasPrefix: (prefix: string) => boolean;
  /** Is this a CTO-level key? (has meta:cto or marketing:cto) */
  isCto: boolean;
  /** Can this key create/publish? (has meta:create) */
  canPublish: boolean;
  /** Loading state */
  loading: boolean;
  /** Error message if permissions couldn't be fetched */
  error: string | null;
  /** Re-fetch on demand */
  refresh: () => void;
}

const PermissionsContext = createContext<PermissionsCtx>({
  scopes: [],
  capabilities: [],
  hasScope: () => false,
  hasPrefix: () => false,
  isCto: false,
  canPublish: false,
  loading: true,
  error: null,
  refresh: () => {},
});

export function PermissionsProvider({ children }: { children: React.ReactNode }) {
  const [scopes, setScopes] = useState<string[]>([]);
  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPerms = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data: PermissionsResponse = await getPermissions();
      setScopes(data.scopes || []);
      setCapabilities(data.capabilities || []);
    } catch (err: any) {
      console.warn('⚠️ No se pudieron cargar permisos Django:', err);
      // Fallback: no scopes → most restrictive view
      setScopes([]);
      setCapabilities([]);
      setError(err.message || 'Error cargando permisos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      fetchPerms();
    } else {
      setLoading(false);
    }
  }, [fetchPerms]);

  const hasScope = useCallback(
    (scope: string): boolean => scopes.includes(scope),
    [scopes],
  );

  const hasPrefix = useCallback(
    (prefix: string): boolean => scopes.some((s) => s.startsWith(prefix)),
    [scopes],
  );

  const hasWildcard = useMemo(() => scopes.includes('*'), [scopes]);

  const isCto = useMemo(
    () => hasWildcard || scopes.some((s) => s === 'meta:cto' || s === 'marketing:cto' || s === 'cto'),
    [scopes, hasWildcard],
  );

  const canPublish = useMemo(
    () => isCto || scopes.includes('meta:create'),
    [scopes, isCto],
  );

  return (
    <PermissionsContext.Provider value={{ scopes, capabilities, hasScope, hasPrefix, isCto, canPublish, loading, error, refresh: fetchPerms }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export const usePermissions = () => useContext(PermissionsContext);
